<template>
  <v-col cols="12" sm="12" :md="cols('md')" :lg="cols('lg')">
    <v-card v-if="!loading">
      <v-card-title>
        Accounts
        <v-card-actions>
          <v-switch
            v-model="activeOnly"
            color="green"
            hide-details
            class="acc-toggle"
          ></v-switch>
        </v-card-actions>
      </v-card-title>
      <v-container pt-0 fluid>
        <v-row>
          <v-col
            class="acc-col"
            v-for="(grp, index) in groups"
            v-bind:key="index"
          >
            <v-card
              v-for="a in grp"
              :disabled="a.inactive"
              :class="[
                'bookie-status',
                {
                  online: !!activeBookieDetails(a.bookie, a.account),
                  needmoney: a.balance === 1,
                  nopriceupdates: missingPriceUpdates(a.bot, a.bookie)
                }
              ]"
              :style="{
                'border-color': botActiveColor(a)
              }"
              :key="a.key"
              flat
              @click="() => toggle(a.bookie)"
            >
              <v-menu absolute offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-list-item two-line v-bind="attrs" v-on="on">
                    <bookie-icon :bookie="a.bookie" :size="30"></bookie-icon>
                    <v-list-item-content pt-0>
                      <v-list-item-title>
                        {{ a.account }}
                        <div
                          class="acc-bets"
                          v-if="hasMaxAttempts(a.bot, a.bookie)"
                        >
                          ({{ betAttempts(a.bot, a.bookie) }})
                        </div>
                      </v-list-item-title>

                      <v-list-item-subtitle class="acc-action">
                        {{ accountSubtitle(a) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>

                    <v-list-item-action>
                      <v-list-item-action-text class="acc-user">
                        <span>
                          {{ currency(a.balance) }}
                        </span>
                      </v-list-item-action-text>
                      <v-list-item-action-text class="acc-schedule">
                        <span>
                          {{ formatSchedule(a) }}
                        </span>
                      </v-list-item-action-text>
                      <!-- <v-list-item-action-text
                        class="acc-bets"
                        v-if="hasMaxAttempts(a.bot, a.bookie)"
                      >
                        <span>

                        </span>
                      </v-list-item-action-text> -->
                    </v-list-item-action>
                  </v-list-item>
                </template>
                <v-list>
                  <v-list-item @click="restart(a.bot, a.bookie)">
                    Restart
                  </v-list-item>
                  <v-list-item @click="screenshots(a.bot, a.bookie)">
                    Screenshot
                  </v-list-item>
                  <v-list-item @click="checkResults(a.bot, a.bookie)">
                    Check Results
                  </v-list-item>
                  <v-list-item @click="refreshUpcomingRaces(a.bot, a.bookie)">
                    Refresh Races
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-card>
    <v-card v-else>
      <v-skeleton-loader class="mb-4" type="card-heading"></v-skeleton-loader>
      <v-skeleton-loader type="list-item-avatar"></v-skeleton-loader>
      <v-skeleton-loader type="list-item-avatar"></v-skeleton-loader>
    </v-card>
  </v-col>
</template>

<script>
import moment from "moment";
import BookieIcon from "../components/BookieIcon";
import { currency, botColor } from "../lib/formatting";
import {
  checkBetResultsForBookie,
  refreshUpcomingRacesForBookie,
  restartBookie,
  screenshotBookie
} from "../lib/commands";

export default {
  name: "AccountsTable",
  data() {
    return {
      toggled: {},
      activeOnly: true
    };
  },
  components: {
    BookieIcon
  },
  props: ["accounts", "loading"],
  computed: {
    displayedAccounts() {
      return this.accounts.filter(a => !this.activeOnly || !a.inactive);
    },
    groups() {
      const sorted = [...this.displayedAccounts].sort((x, y) => {
        const xOnline = this.activeBookieDetails(x.bookie, x.account) ? -1 : 1;
        const yOnline = this.activeBookieDetails(y.bookie, y.account) ? -1 : 1;

        if (xOnline < yOnline) return -1;
        if (xOnline > yOnline) return 1;

        if (x.inactive && !y.inactive) return 1;
        if (y.inactive && !x.inactive) return -1;

        if (x.balance < y.balance) return 1;
        if (x.balance > y.balance) return -1;
        return 0;
      });

      const groupSize = this.accountsPerCol();
      const groups = [];
      for (let i = 0; i <= groupSize; i++) {
        const grp = sorted.slice(i * groupSize, i * groupSize + groupSize);
        if (grp.length) groups.push(grp);
      }

      return groups;
    }
  },
  methods: {
    async restart(bot, bookie) {
      try {
        const resp = await restartBookie(bot, bookie);
        if (resp.successful) {
          this.showSuccess(`${bookie} successfully restarted`);
        } else {
          this.showError(resp.message);
        }
      } catch (err) {
        this.showError(err);
      }
    },
    async screenshots(bot, bookie) {
      try {
        const resp = await screenshotBookie(bot, bookie);
        if (resp.successful) {
          this.showSuccess(`${bookie} screenshots sent`);
        } else {
          this.showError(resp.message);
        }
      } catch (err) {
        this.showError(err);
      }
    },
    async checkResults(bot, bookie) {
      try {
        const resp = await checkBetResultsForBookie(bot, bookie);
        if (resp.successful) {
          this.showSuccess(`${bookie} results checked`);
        } else {
          this.showError(resp.message);
        }
      } catch (err) {
        this.showError(err);
      }
    },
    async refreshUpcomingRaces(bot, bookie) {
      try {
        const resp = await refreshUpcomingRacesForBookie(bot, bookie);
        if (resp.successful) {
          this.showSuccess(`${bookie} races refreshed`);
        } else {
          this.showError(resp.message);
        }
      } catch (err) {
        this.showError(err);
      }
    },
    isToggled(bookie) {
      return !!this.toggled[bookie];
    },
    toggle(bookie) {
      this.toggled[bookie] = this.toggled[bookie] ? false : true;
      for (const b of Object.keys(this.toggled)) {
        if (b !== bookie) {
          this.toggled[b] = false;
        }
      }
    },
    missingPriceUpdates(bot, bookie) {
      const priceUpdates = this.$store.getters.getBookiePriceUpdates(
        bot,
        bookie
      );
      return priceUpdates === 0;
    },
    activeBookieDetails(bookie, username) {
      return this.$store.getters.onlineBookies.find(
        b => b.bookie === bookie && b.username === username
      );
    },
    hasMaxAttempts(bot, bookie) {
      return !!this.$store.getters.betAttempts(bot, bookie).max_attempts;
    },
    betAttempts(bot, bookie) {
      const { max_attempts, current } = this.$store.getters.betAttempts(
        bot,
        bookie
      );
      return `${current}/${max_attempts}`;
    },
    accountSubtitle(acc) {
      const b = this.activeBookieDetails(acc.bookie, acc.account);
      if (b && b.action) {
        const formatted = this.formatAction(b.action);
        if (formatted) return formatted;
      }

      if (acc.balance === 1) {
        return "Account empty - deposit funds";
      }

      return "";
    },
    currency(amount) {
      return currency(amount);
    },
    formatSchedule(acc) {
      const online = !!this.activeBookieDetails(acc.bookie, acc.account);
      if (online) return "";

      const lastUpdate = new Date(acc.last_update).valueOf();
      const now = new Date().valueOf();

      // dont show the schedule if it hasnt run for more than 48 hours
      if (now - lastUpdate > 48 * 60 * 60 * 1000) return "";

      const { max_attempts, current } = this.$store.getters.betAttempts(
        acc.bot,
        acc.bookie
      );

      // max attempts reached - wont be back online
      if (max_attempts > 0 && current >= max_attempts) return "";

      for (let i = 0; i < acc.schedule.length; i++) {
        const time = acc.schedule[i];
        if (time.length < 2) continue; // invalid time

        if (time[0] > now) {
          return `${i === 0 ? "Starting" : "Resuming"} at ${moment(
            time[0]
          ).format("h:mm A")}`;
        }
      }

      return "";
    },
    formatAction(action) {
      switch (action) {
        case "LOAD_RACE":
          return "Opening race...";
        case "UPCOMING_RACES":
          return "Checking for upcoming races...";
        case "CHECK_RESULTS":
          return "Checking bet results...";
        case "PLACE_BET":
          return "Placing a bet...";
        default:
          return "";
      }
    },
    maxColumns() {
      if (this.$vuetify.breakpoint.xs) return 1;
      if (this.$vuetify.breakpoint.sm) return 2;
      if (this.$vuetify.breakpoint.md) return 3;
      if (this.$vuetify.breakpoint.lg || this.$vuetify.breakpoint.xl) return 4;
    },
    accountsPerCol() {
      const numAccounts = this.displayedAccounts.length;
      const perCol = Math.ceil(numAccounts / this.maxColumns());
      return Math.max(6, perCol);
    },
    cols(size) {
      if (size === "md") {
        return Math.min(12, 4 * this.groups.length);
      } else if (size === "lg") {
        return Math.min(12, 3 * this.groups.length);
      } else {
        return 12;
      }
    },
    botActiveColor({ bot }) {
      return botColor(bot);
    }
  }
};
</script>

<style lang="scss">
#home {
  .acc-col {
    padding: 0px;

    div.v-card {
      border-radius: 0px;

      &.bookie-status {
        border-left: 4px solid;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;

        &.nopriceupdates {
          background: linear-gradient(
            -135deg,
            rgba(255, 0, 0, 0.7) 8px,
            white 0
          );
        }
      }

      &.needmoney {
        background-color: rgba(255, 0, 0, 0.15);
      }

      &.online {
        background-color: rgba(0, 255, 0, 0.15);

        &.nopriceupdates {
          background: linear-gradient(
            -135deg,
            rgba(255, 0, 0, 0.7) 8px,
            rgba(0, 255, 0, 0.15) 0
          );
        }
      }
    }
  }

  .acc-action {
    padding-top: 0px;
    font-size: 0.8em;
    font-weight: 400;
  }

  .acc-schedule {
    padding-top: 2px;
    font-size: 0.75em;
    font-weight: 400;
  }

  .acc-user {
    font-size: 0.9em;
  }

  .acc-bets {
    display: inline-block;
    margin-left: 8px;
    font-size: 0.8em;
  }

  .acc-command {
    display: inline-block;
    margin: 10px;
  }

  .acc-toggle {
    position: absolute;
    left: 120px;
    margin-top: 0px;
  }
}
</style>
